<script>
    import { navigate } from './lib/router/router.js';
    import {onMount} from 'svelte';
    import Modal from './lib/Modal.svelte';
    import {_, isLoading, locale, locales} from 'svelte-i18n';
    import Loader from './lib/Loader.svelte';
    import {game_name, login_route} from './constants';
    import {logMetric} from './metrics';
    import Fa from 'svelte-fa';
    import {faCircleQuestion, faGlobe, faXmark} from '@fortawesome/free-solid-svg-icons';
    import {user} from './stores';
    import {tagNetworkFailure} from './lib/network-failure.js';
    import {readLoginResponse} from './lib/login-response.js';
    import {getCurrentTheme} from './themeLoader.js';
    import SnowDecoration from './lib/SnowDecoration.svelte';

    // returnTo: where a successful login lands; children: a notice shown above the form
    // (pages that render <Login/> in place, like /firstborn).
    let { returnTo = '/supplies', children } = $props();

    const currentTheme = getCurrentTheme();
    const isWinterTheme = currentTheme === 'winter_theme';
    const iconColor = isWinterTheme ? '#96e3ff' : 'gold';

    let username = $state('');
    let password = $state('');
    let loading = $state(false);
    let login_details_info = $state(false);
    // Once the guide has been opened the help link stops jiggling.
    let login_details_seen = $state(false);
    let remember_player_name = $state(false);
    let remember_check_box;

    let modal_hide = () => {
        login_details_info = false;
    };

    let loginResponse = $state({
        error: null,
        user: null,
    });

    $effect(() => {
        if ($locale === null) return;
        logMetric({language: $locale});
    });

    onMount(() => {
        username = localStorage.player_name ? localStorage.getItem('player_name') : '';
        remember_player_name = localStorage.player_name ? true : false;
    });

    const handleSubmit = () => {
        let loginFields = {
            username,
            password,
        };
        loading = true;
        loginResponse = {
            error: null,
            user: null,
        };
        fetch(login_route, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(loginFields),
        })
            .catch(tagNetworkFailure)
            .then(readLoginResponse)
            .then((data) => {
                if (data.statusCode) {
                    loginResponse = {
                        ...loginResponse,
                        error: data.message,
                    };
                } else {
                    loginResponse = {
                        ...loginResponse,
                        user: data.user,
                    };
                    localStorage.removeItem('user');
                    localStorage.removeItem('player_name');
                    loginResponse.user.username = username;
                    user.set(loginResponse);
                    if (remember_player_name) {
                        localStorage.setItem('player_name', username);
                    }
                    localStorage.setItem('user', JSON.stringify(loginResponse));

                    Rollbar.configure({
                        payload: {
                            person: {
                                id: data.user.id, // required
                                username: username,
                            }
                        }
                    });
                    
                    navigate(returnTo);
                }
            })
            .catch((error) => {
                // Rollbar #2532 — the form used to swallow these: nothing was rendered and
                // a plain connection drop was reported at critical. The user always gets a
                // message; only non-network failures (a real bug shape) stay critical.
                if (error?.isNetworkFailure) {
                    loginResponse = {
                        ...loginResponse,
                        error: $_('NoInternetConnectionTitle') + '. ' + $_('NoInternetConnectionDescription'),
                    };
                    Rollbar.info('Login network failure: ' + error);
                } else {
                    loginResponse = {
                        ...loginResponse,
                        error: $_('PleaseTryAgainLater'),
                    };
                    // A status here is an error page in front of the endpoint (Rollbar #715, #498):
                    // named like silent login's "Login failed: 502", not as the bare Error.
                    Rollbar.critical(error?.status ? 'Login error: ' + error : error);
                }
            })
            .finally(() => {
                logMetric({
                    login: {
                        username: username,
                        ...(loginResponse.error !== null && {error: loginResponse.error})
                    },
                });
                loading = false;
            });
    };
</script>
<div class="user-select">
    <Fa fw icon={faGlobe} primaryColor={iconColor}/>
    <select bind:value={$locale} class="selector">
        {#each $locales as locale}
            <option value={locale}>{locale.toUpperCase()}</option>
        {/each}
    </select>
</div>
<div class="login">
    {#if $isLoading}
        <Loader/>
    {:else}
        <div class="login">
            <a href="#home" onclick={() => navigate(-1)}><img src="/images/logo.png" class="logo"
                                                               alt={game_name}
                                                               title={game_name}/></a>
            <div class="form">
                <h1>{$_('Login')}</h1>
                {@render children?.()}
                {#if loginResponse.error}
                    <div class="error">
                        {loginResponse.error}
                    </div>
                {/if}
                <form
                        onsubmit={(e) => { e.preventDefault(); handleSubmit(e); }}
                        autocomplete="on"
                        class="flex flex-column"
                >
                    <p class="login-name">
                        <input
                                id="username"
                                name="username"
                                bind:value={username}
                                placeholder="Player Name"
                                class="input-field"
                                autocorrect="off"
                                autocapitalize="none"
                                autocomplete="username"
                                type="search"
                                required
                        />
                    </p>
                    <p class="login-password">
                        <input
                                id="password"
                                name="password"
                                bind:value={password}
                                placeholder="Token"
                                class="input-field"
                                autocorrect="off"
                                autocapitalize="none"
                                autocomplete="off"
                                type="search"
                                required
                        />
                    </p>
                    <!-- svelte-ignore a11y_invalid_attribute -->
                    <a
                            onclick={() => { login_details_info = true; login_details_seen = true; }}
                            class="login-info"
                            class:seen={login_details_seen}
                            href="javascript:"><Fa icon={faCircleQuestion} fw/>How to find your login details?</a
                    >
                    <label>
                        <input type=checkbox bind:checked={remember_player_name}>
                        Remember player name
                    </label>
                    <button type="submit" class="button primary blue" disabled={loading}>
                        <SnowDecoration/>
                        {$_('AccountSignIn')}</button>
                    <small>
                        {@html $_('acceptPPandToS', {
                            values: {
                                ToS:
                                    '<a target="_blank" href="/terms-of-service">' +
                                    $_('TermsOfService') +
                                    '</a>',
                                PP:
                                    '<a target="_blank" href="/privacy-policy">' +
                                    $_('PrivacyPolicy') +
                                    '</a>',
                            },
                        })}
                    </small>
                </form>
            </div>
            <Modal bind:open={login_details_info}>
                <div class="card howto">
                    <!-- svelte-ignore a11y_invalid_attribute -->
                    <a href="javascript:" onclick={modal_hide} class="close-button">
                        <Fa icon={faXmark} fw size="2x" primaryColor="#240c05"/>
                    </a>
                    <!-- Screen recording of Player Profile > Account; muted + playsinline so it
                         autoplays on iOS. The width/height fix the box before it loads. -->
                    <video
                            src="/videos/login-guide.mp4"
                            class="logo twoFactor guide-video"
                            width="720"
                            height="1280"
                            autoplay
                            muted
                            loop
                            playsinline
                            title={game_name}
                    ></video>
                    <p>
                        You can find and copy your authentication token in your Player Profile
                        > Account section.
                    </p>
                    <button type="button" class="button primary blue" onclick={modal_hide} disabled={loading}
                    >{$_('Close')}</button
                    >
                </div>
            </Modal>
        </div>
    {/if}
</div>
<style lang="scss">
  @use './styles/vars.scss' as *;

  :global {
    .howto {
      position: relative;

      p {
        font-weight: bold;
      }

      .close-button {
        position: absolute;
        top: 0;
        right: 0;
        padding: 7px 2px;
        margin: 0;
      }

      .twoFactor {
        max-height: 400px;
      }

      .guide-video {
        display: block;
        width: auto;
        height: 400px;
        max-width: 100%;
        margin: 0 auto;
      }
    }

    [data-pincode] {
      display: flex;
      border: none;
      // gap: 5px;
      margin-bottom: 10px;

      input {
        width: 3rem;
        padding: 0.5rem 1rem;
        margin: 0;
        border-radius: 0 !important;
        text-align: center;
        font-family: var(--font-family-mono) !important;
        transition: all 0.2s ease-in-out;
        border: 1px solid rgba(0, 0, 0, 0.2) !important;
        background-color: white;
        background-image: linear-gradient(
                        180deg,
                        rgba(0, 0, 0, 0.02) 0%,
                        rgba(255, 255, 255, 0.02) 50%
        );
        box-shadow: inset 0 1px 2px rgb(0 0 0 / 20%);

        &:first-child {
          border-radius: 4px 0 0 4px !important;
        }

        &:last-child {
          border-radius: 0 4px 4px 0 !important;
        }

        &:hover {
          box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.2),
          0 0 5px var(--color-primary) !important;
          z-index: 2;
        }

        &:focus {
          outline: 3px solid var(--color-primary) !important;
          box-shadow: 0 0 5px var(--color-primary) !important;
          z-index: 1;
        }

        &:not(:last-of-type) {
          border-right: 1px solid #e0e0e0;
        }
      }
    }

    .login-name, .login-password {
      color: #000000 !important;
    }

    .input-field {
      margin-bottom: 10px;
      font-family: var(--font-family-mono) !important;
      transition: all 0.2s ease-in-out;

      &:hover {
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.2),
        0 0 5px var(--color-primary) !important;
      }

      &:focus {
        outline: 3px solid var(--color-primary) !important;
        box-shadow: 0 0 5px var(--color-primary) !important;
      }
    }

    .button {
      margin-top: 10px;
      width: 100%;
    }

    .button.blue {
      padding: 1.4rem 2.5rem;
      color: $color-dark-text;
      background: rgb(67, 255, 255) radial-gradient(
                      ellipse at center,
                      #43ffff 10%,
                      #2182cb 100%
      );

      .snow3 {
        position: absolute;
        z-index: 15;
        top: -9px;
        left: 0;
        right: 0;
        margin: 0 auto;
        transform: scale(0.6);
      }
    }

    .user-select {
      position: absolute;
      top: 10px;
      right: 10px;
      border: 1px solid $light-color;
      border-radius: 5px;
      opacity: 0.6;
      transition: all 0.2s ease-in-out;
      z-index: 2;
      display: flex;
      align-items: center;

      &:hover {
        opacity: 1;
        box-shadow: 0 0 5px var(--color-primary) !important;
      }

      .svelte-fa {
        position: absolute;
        left: 5px;
        top: 7px;
        z-index: 1;
        pointer-events: none;
      }

      .selector {
        height: 30px;
        min-width: 70px;
        display: inline-block;
        margin: 0;
        border: none;
        background-color: transparent;
        box-shadow: none;
        text-transform: uppercase;
        color: $light-color;
        padding: 0 30px 0 25px;
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOTIuNCIgaGVpZ2h0PSIyOTIuNCI+PHBhdGggZmlsbD0iI0ZGRDcwMCIgZD0iTTI4NyA2OS40YTE3LjYgMTcuNiAwIDAgMC0xMy01LjRIMTguNGMtNSAwLTkuMyAxLjgtMTIuOSA1LjRBMTcuNiAxNy42IDAgMCAwIDAgODIuMmMwIDUgMS44IDkuMyA1LjQgMTIuOWwxMjggMTI3LjljMy42IDMuNiA3LjggNS40IDEyLjggNS40czkuMi0xLjggMTIuOC01LjRMMjg3IDk1YzMuNS0zLjUgNS40LTcuOCA1LjQtMTIuOCAwLTUtMS45LTkuMi01LjUtMTIuOHoiLz48L3N2Zz4=');
        background-repeat: no-repeat, repeat;
        background-position: right -1em top 50%, 0 0;
        background-size: 0.65em auto, 100%;
        font-size: 14px;
        cursor: pointer;
        outline: none;

        &:focus {
          outline: none;
        }
      }
    }
  }

  // One short shake at the start of each 8s cycle, then still.
  @keyframes login-help-nudge {
    0%, 12%, 100% {
      transform: none;
    }
    2%, 6% {
      transform: rotate(-3deg) scale(1.08);
    }
    4%, 8% {
      transform: rotate(3deg) scale(1.08);
    }
    10% {
      transform: rotate(-1deg) scale(1.02);
    }
  }

  .login {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    // height: 80vh;
    position: relative;

    label {
      text-align: left;
      display: flex;
      align-items: center;
      font-size: 12px;

      input {
        margin-left: 10px;
        margin-right: 10px;
      }
    }

    small {
      font-size: 0.7em;
      color: #fff !important;
      line-height: 1.4em;
      display: block;
      opacity: 0.6;
      padding: 0 15px;
      text-align: center;
      margin-top: 10px;
      text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3);
      transition: opacity 0.2s ease-in-out;

      :global(a) {
        color: #ffdd57 !important;
        text-decoration: underline;
      }

      &:hover {
        opacity: 1;
      }
    }

    .error {
      font-size: 1.4rem;
      margin-bottom: 1rem;
      background: rgb(221 26 0 / 62%);
      border-radius: 4px;
      padding: 4px 8px;
      color: #fffce9;
      border: 2px solid #ca2000;
      text-shadow: 0 0 1px #000;
      text-transform: capitalize;
      box-shadow: 0 0 20px #ff1e0285;
    }

    // Right under the Token field, where players get stuck. Still here after a minute, it
    // jiggles every 8s until the guide is opened (CSS only).
    .login-info {
      align-self: center;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin: 0 0 12px;
      font-size: 15px;
      font-weight: 700;
      color: #ffdd57;
      text-decoration: underline;
      text-underline-offset: 3px;
      text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3);
      animation: login-help-nudge 8s ease-in-out 60s infinite;

      &.seen {
        animation: none;
      }

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }

    h1 {
      color: #ffb900;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    }

    .form {
      width: 300px;
      margin-top: -50px;
    }

    .logo {
      max-width: 350px;
      min-height: 299px;
    }
  }
</style>
